import { createColumnHelper } from '@tanstack/react-table';
import groupBy from 'lodash/groupBy';
import maxBy from 'lodash/maxBy';
import startCase from 'lodash/startCase';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';
import React, { useMemo, useState } from 'react';
import { LuDownload } from 'react-icons/lu';
import styled from 'styled-components';

import { NdvrEmploymentStatus } from '@ndvr/customer-api';
import { JournalEvent, JournalEventType, UserType } from '@ndvr/ncrm-api';

import { Button as UiButton } from '@/components/ui/button';
import { JOURNAL_EVENT_MAP } from 'modules';
import { addMonthToToday, getPersonName, todayDate } from 'utils';
import { usePersonProfilesForTenant } from 'views/person/person-queries';
import {
    Button,
    Card,
    CustomTablePage,
    DateTimeDisplay,
    Form,
    FormRadioButtons,
    FormSection,
    FormSelect,
    FormToggle,
    HorizontalLayout,
    PageSpinner,
    TableFilters,
    Text,
    Title,
    useTableFilter,
    VerticalLayout,
} from 'widgets';
import { GlobalSearchTermInput } from 'widgets/form/filter/GlobalSearchTermInput';
import { FormDateInput } from 'widgets/form/input/FormDateInput';
import { exportTableToCsv } from 'widgets/table/exportTableToCsv';

import { useJournalEvents } from '../dev-queries';

const columnHelper = createColumnHelper<JournalEvent>();

enum StatusFilterState {
    CLIENT = 'Client',
    EXTERNAL = 'External',
    EXTERNAL_WO_CLIENTS = 'External w/o Clients',
    ADVISOR = 'Advisor',
    EMPLOYEE_CURRENT = 'Current Employee',
    EMPLOYEE_FORMER = 'Former Employee',
    ALL = 'Show All',
}

export const Journal = () => {
    const [dateRange, setDateRange] = useState({ startDate: addMonthToToday(-1), endDate: todayDate() });
    const { data } = useJournalEvents(dateRange.startDate, dateRange.endDate);
    const { data: personProfiles } = usePersonProfilesForTenant();

    const columns = [
        columnHelper.accessor((journalEvent) => startCase(journalEvent.type.toLowerCase()), {
            id: 'type',
            header: 'Type',
            size: 175,
            enableSorting: false,
        }),
        columnHelper.accessor((journalEvent) => startCase(journalEvent.source.toLowerCase()), {
            id: 'source',
            header: 'Source',
            size: 175,
            enableSorting: false,
        }),
        columnHelper.accessor(
            (journalEvent) =>
                getPersonName(
                    personProfiles?.find(
                        ({ personOrgShortProfile }) =>
                            personOrgShortProfile.personOrgGUID === journalEvent.loggedInPersonOrgGUID,
                    )?.personOrgShortProfile,
                ) || journalEvent.loggedInPersonOrgGUID,
            {
                id: 'loggedInPersonOrgGUID',
                header: 'Person',
                sortingFn: 'alphanumeric',
            },
        ),
        columnHelper.accessor((journalEvent) => journalEvent.globalAccountGUID || '', {
            id: 'globalAccountGUID',
            header: 'Household',
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor('portfolioGUID', {
            id: 'portfolioGUID',
            header: 'Portfolio',
            sortingFn: 'alphanumeric',
        }),
        columnHelper.accessor((journalEvent) => journalEvent.sessionGUID?.substring(0, 8) || '', {
            id: 'sessionGUID',
            header: 'Session',
            sortingFn: 'alphanumeric',
            size: 150,
        }),
        columnHelper.accessor('timestamp', {
            id: 'timestamp',
            header: 'Timestamp',
            cell: ({ getValue }) => <DateTimeDisplay dateString={getValue()} />,
            sortingFn: 'datetime',
            size: 150,
        }),
    ];

    const filters: TableFilters<JournalEvent> = {
        type: {
            label: 'Type',
            filterFunction: (journalEntry, filterValue) => journalEntry.type === filterValue,
            valueDecoder: (filterValue) => startCase(filterValue.toLowerCase()),
        },
        source: {
            label: 'Source',
            filterFunction: (journalEntry, filterValue) => {
                return startCase(journalEntry.source) === filterValue;
            },
            valueDecoder: (filterValue) => startCase(filterValue.toLowerCase()),
        },
        userStatus: {
            label: 'User Status',
            filterFunction: (journalEntry, filterValue) => {
                const profile = personProfiles?.find(
                    ({ personOrgShortProfile }) =>
                        personOrgShortProfile.personOrgGUID === journalEntry.loggedInPersonOrgGUID,
                )?.personOrgShortProfile;
                switch (filterValue) {
                    case StatusFilterState.CLIENT:
                        return journalEntry.userType === UserType.CLIENT;
                    case StatusFilterState.EXTERNAL:
                        return !profile?.advisor && !profile?.ndvrEmploymentStatus;
                    case StatusFilterState.EXTERNAL_WO_CLIENTS:
                        return (
                            !profile?.advisor &&
                            !profile?.ndvrEmploymentStatus &&
                            journalEntry.userType !== UserType.CLIENT
                        );
                    case StatusFilterState.ADVISOR:
                        return profile?.advisor === true;
                    case StatusFilterState.EMPLOYEE_CURRENT:
                        return profile?.ndvrEmploymentStatus === NdvrEmploymentStatus.CURRENT_EMPLOYEE;
                    case StatusFilterState.EMPLOYEE_FORMER:
                        return profile?.ndvrEmploymentStatus === NdvrEmploymentStatus.FORMER_EMPLOYEE;
                    case StatusFilterState.ALL:
                }
                return true;
            },
            valueDecoder: (filterValue) => filterValue.toString(),
        },
    };

    const { FilterButton, FilterBar, filteredData } = useTableFilter(data, filters, {
        userStatus: StatusFilterState.ALL,
    });

    const sources = useMemo(() => uniq(data?.map(({ source }) => source)), [data]);

    return (
        <VerticalLayout $gap={16}>
            <HorizontalLayout $gap={16} $apart>
                <Card>
                    <Form submitOnChange defaultValues={dateRange} onSubmit={(d) => setDateRange(d)} labelWidth="80px">
                        <HorizontalLayout $gap={48}>
                            <FormDateInput name="startDate" label="Start date" preventFlip />
                            <FormDateInput name="endDate" label="End date" preventFlip />
                        </HorizontalLayout>
                    </Form>
                </Card>
                <HorizontalLayout $rowCenter $gap={16}>
                    <GlobalSearchTermInput />
                    <FilterButton>
                        <FormSection>
                            <VerticalLayout $gap={16}>
                                <FormSelect label="Event category" name="type">
                                    <option></option>
                                    {Object.keys(JournalEventType).map((journalEventType) => (
                                        <option key={journalEventType} value={journalEventType}>
                                            {startCase(journalEventType.toLowerCase())}
                                        </option>
                                    ))}
                                </FormSelect>
                                <FormSelect label="Event source" name="source">
                                    <option></option>
                                    {sources.map((source) => (
                                        <option key={source}>{startCase(source.toLowerCase())}</option>
                                    ))}
                                </FormSelect>
                                <FormRadioButtons
                                    name="userStatus"
                                    title="User status"
                                    values={[
                                        {
                                            label: StatusFilterState.CLIENT,
                                            value: StatusFilterState.CLIENT,
                                        },
                                        {
                                            label: StatusFilterState.EXTERNAL,
                                            value: StatusFilterState.EXTERNAL,
                                        },
                                        {
                                            label: StatusFilterState.EXTERNAL_WO_CLIENTS,
                                            value: StatusFilterState.EXTERNAL_WO_CLIENTS,
                                        },
                                        {
                                            label: StatusFilterState.ADVISOR,
                                            value: StatusFilterState.ADVISOR,
                                        },
                                        {
                                            label: StatusFilterState.EMPLOYEE_CURRENT,
                                            value: StatusFilterState.EMPLOYEE_CURRENT,
                                        },
                                        {
                                            label: StatusFilterState.EMPLOYEE_FORMER,
                                            value: StatusFilterState.EMPLOYEE_FORMER,
                                        },
                                        {
                                            label: StatusFilterState.ALL,
                                            value: StatusFilterState.ALL,
                                        },
                                    ]}
                                />
                            </VerticalLayout>
                        </FormSection>
                    </FilterButton>
                </HorizontalLayout>
            </HorizontalLayout>
            {data ? (
                <>
                    {FilterBar}

                    <MarketingFunnel events={filteredData} />

                    <CustomTablePage
                        defaultSortCol="timestamp"
                        columns={columns}
                        data={filteredData}
                        exportName="journal"
                        toolbarPosition="header"
                        renderToolbar={(table) => (
                            <UiButton
                                variant="outline"
                                size="sm"
                                onClick={() => exportTableToCsv({ table, exportName: 'journal' })}
                            >
                                <LuDownload className="h-4 w-4" />
                                Export CSV
                            </UiButton>
                        )}
                    />
                </>
            ) : (
                <PageSpinner />
            )}
        </VerticalLayout>
    );
};

const funnelEventTypes = [
    JournalEventType.CHANGE_PASSWORD,
    JournalEventType.SIGN_IN,
    JournalEventType.CREATE_PORTFOLIO,
    JournalEventType.CHANGE_REQUIREMENTS,
    JournalEventType.CHANGE_PREFERENCES,
    JournalEventType.RUN_OPTIMIZATION,
];

const MarketingFunnel = ({ events }: { events: JournalEvent[] }) => {
    const groupedByType = groupBy(events, 'type');
    const [showContent, setShowContent] = useState(false);
    const [groupByPersons, setGroupByPersons] = useState(false);

    const maxCount =
        (groupByPersons
            ? uniqBy(groupedByType['SIGN_IN'], 'loggedInPersonOrgGUID')?.length
            : maxBy(Object.values(groupedByType), (arr) => arr.length)?.length) || 1;

    return (
        <Card>
            <VerticalLayout $gap={16}>
                <HorizontalLayout $apart $rowCenter>
                    <Title content>Stats</Title>
                    <HorizontalLayout $gap={128}>
                        <Form submitOnChange onSubmit={() => setGroupByPersons((current) => !current)}>
                            <FormToggle name="groupByPersons" label="Group by Persons" />
                        </Form>
                        <Button size="small" onClick={() => setShowContent((current) => !current)}>
                            {showContent ? 'Hide' : 'Show'}
                        </Button>
                    </HorizontalLayout>
                </HorizontalLayout>

                {showContent && (
                    <VerticalLayout $gap={4}>
                        {funnelEventTypes.map((type) => {
                            const count =
                                (groupByPersons
                                    ? uniqBy(groupedByType[type], 'loggedInPersonOrgGUID')?.length
                                    : groupedByType[type]?.length) || 0;
                            const percentage = ((count / maxCount) * 100).toFixed(2);

                            return (
                                <Bar $width={percentage} key={type}>
                                    <Text>
                                        {JOURNAL_EVENT_MAP[type]} - {count} ({percentage}%)
                                    </Text>
                                </Bar>
                            );
                        })}
                    </VerticalLayout>
                )}
            </VerticalLayout>
        </Card>
    );
};

const Bar = styled.div<{ $width: string }>`
    white-space: nowrap;
    padding: 8px;
    width: ${({ $width }) => $width}%;
    background: ${({ theme }) => theme.colors.seafoam200};
`;
